import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, GraduationCap, Lock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function Course1120Exam() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/course1120/exam")
      .then(r => setQuestions(r.data))
      .catch(e => { if (e?.response?.status === 402) setLocked(true); })
      .finally(() => setLoading(false));
  }, []);

  const submit = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error(`Please answer all ${questions.length} questions before submitting.`);
      return;
    }
    setSubmitting(true);
    try {
      const arr = questions.map((_, i) => answers[i]);
      const r = await api.post("/course1120/exam/submit", { answers: arr });
      setResult(r.data);
      if (r.data.passed) {
        toast.success("You passed! Your certificate is ready.");
        setTimeout(() => nav("/course/1120/certificate"), 1800);
      } else {
        toast.error(`${r.data.percent}% — 80% required to pass. Review the missed topics and try again.`);
      }
    } finally { setSubmitting(false); }
  };

  if (loading) return <Layout><div className="max-w-3xl mx-auto px-6 py-14 text-muted-foreground">Loading exam…</div></Layout>;

  if (locked) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 py-14 text-center space-y-5" data-testid="exam-1120-paywall">
          <Lock size={48} className="mx-auto text-primary" />
          <div className="font-serif text-4xl">Final exam is locked</div>
          <p className="text-muted-foreground">Enroll in Mastering Form 1120 to take the 20-question exam and earn your certificate.</p>
          <Button asChild data-testid="exam-1120-paywall-buy"><Link to="/pricing?plan=corporate">Enroll — $250</Link></Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="exam-1120-page">
        <div className="mb-2">
          <Link to="/course/1120" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <ArrowLeft size={12} /> Back to course
          </Link>
        </div>
        <div className="mb-8">
          <div className="label-caps text-primary flex items-center gap-2"><GraduationCap size={14} /> Final Exam</div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-none">Mastering Form 1120</h1>
          <p className="text-muted-foreground mt-3">20 multiple-choice questions. 80% to pass. You can retry as many times as you need.</p>
        </div>

        <div className="space-y-6">
          {questions.map((q, i) => (
            <Card key={i} className="p-5" data-testid={`exam-1120-q-${i}`}>
              <div className="label-caps text-primary text-[10px] mb-1">Question {i + 1} of {questions.length}</div>
              <div className="font-medium mb-3">{q.q}</div>
              <div className="space-y-2">
                {q.options.map((opt, j) => (
                  <label
                    key={j}
                    className={`flex items-start gap-2 p-2 rounded border cursor-pointer text-sm ${answers[i] === j ? "border-primary bg-primary/5" : "border-border hover:bg-accent/5"}`}
                    data-testid={`exam-1120-q-${i}-opt-${j}`}
                  >
                    <input
                      type="radio"
                      name={`q-${i}`}
                      checked={answers[i] === j}
                      onChange={() => setAnswers({ ...answers, [i]: j })}
                      className="mt-1"
                      disabled={!!result}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </Card>
          ))}
        </div>

        <div className="mt-8">
          {!result && (
            <Button onClick={submit} disabled={submitting} data-testid="exam-1120-submit" size="lg">
              {submitting ? "Grading…" : "Submit exam"}
            </Button>
          )}
          {result && (
            <Card className={`p-6 border ${result.passed ? "border-primary/40 bg-primary/5" : "border-red-400 bg-red-50"}`} data-testid="exam-1120-result">
              <div className="text-center">
                <div className="label-caps text-primary">{result.passed ? "Passed" : "Not yet"}</div>
                <div className="font-serif text-5xl mt-2">{result.percent}%</div>
                <div className="text-sm text-muted-foreground mt-1">{result.score} / {result.total} correct · 80% required</div>
                <div className="mt-4 flex gap-2 justify-center flex-wrap">
                  {result.passed
                    ? <Button asChild data-testid="exam-1120-cert-link"><Link to="/course/1120/certificate">See my certificate →</Link></Button>
                    : <Button variant="outline" onClick={() => { setResult(null); setAnswers({}); }} data-testid="exam-1120-retry">Try again</Button>
                  }
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}
